import { createURL } from '@sm/utils';
import { t } from '@sm/intl';
import NAVSUBMENUCOPY from './NavSubmenuCopy';
import NAVBARCOPY from '../NavBar/NavBarCopy';
import { showBlog, usabillaLink } from '../../../helpers/languageHelpers';

/**
 * Return translated copy for the given title
 * @param {string} submenuKey
 * @return {Object}
 */
export const getSubmenuTitle = submenuKey =>
  ({
    Products: t(NAVSUBMENUCOPY.PRODUCTS_TITLE),
    Solutions: t(NAVSUBMENUCOPY.SOLUTIONS_TITLE),
    Resources: t(NAVSUBMENUCOPY.RESOURCES_TITLE)
  }[submenuKey]);

/**
 * Gets all Product submenu Items
 * @param {string} lang
 * @param {Boolean} isEUDC
 * @return {Array}
 */
export const getProductsItems = (lang, isEUDC) => {
  return [
    lang === 'en' && [
      {
        title: 'Surveys'
      },
      {
        title: 'SurveyMonkey',
        subTitle:
          'Create & send surveys with the world’s leading online survey software',
        url: createURL('/mp/take-a-tour/', { ut_source: 'megamenu' })
      },
      {
        title: 'Enterprise',
        subTitle: 'Empower your organization with our secure survey platform',
        url: createURL('/enterprise/', { ut_source: 'megamenu' })
      },
      {
        title: 'Integrations & plug-ins',
        subTitle: 'Bring survey insights into your business apps',
        url: createURL('/apps/', { ut_source: 'megamenu' })
      }
    ],
    lang === 'en' &&
      [
        {
          title: 'Specialized products'
        },
        !isEUDC && {
          title: 'Audience',
          subTitle: 'Collect survey responses from our global consumer panel',
          url: createURL('/mp/audience/', { ut_source: 'megamenu' })
        },
        {
          title: 'CX',
          subTitle: 'Understand & improve customer experience (NPS®)',
          url: createURL('/cx/', { ut_source: 'megamenu' })
        },
        {
          title: 'Engage',
          subTitle: 'Measure & increase employee engagement',
          url: createURL('https://engage.surveymonkey.com/', {
            ut_source: 'megamenu'
          })
        },
        {
          title: 'Usabilla',
          subTitle: 'Get in-the-moment feedback across all digital channels',
          url: createURL(usabillaLink(lang), {
            utm_source: 'megamenu'
          })
        }
      ].filter(Boolean),
    lang === 'en' && [
      {
        title: ''
      },
      {
        title: 'TechValidate',
        subTitle: 'Create marketing content from customer feedback',
        url: createURL('https://www.techvalidate.com/', {
          ut_source: 'megamenu'
        })
      },
      {
        title: 'Apply',
        subTitle: 'Collect, review, & manage applications online',
        url: createURL('https://apply.surveymonkey.com/', {
          ut_source: 'megamenu'
        })
      },
      {
        title: 'Wufoo',
        subTitle: 'Gather data & accept payments with online forms',
        url: createURL('https://www.wufoo.com/', { ut_source: 'megamenu' })
      },
      {
        title: 'GetFeedback',
        subTitle: 'Customer feedback for Salesforce',
        url: createURL('https://www.getfeedback.com/', {
          utm_source: 'surveymonkey'
        }),
        openInNewWindow: true
      }
    ],
    lang === 'fr' && [
      {
        title: t(NAVSUBMENUCOPY.SURVEYS)
      },
      {
        title: 'SurveyMonkey',
        subTitle: t(NAVSUBMENUCOPY.SURVEYMONKEY_SUBTITLE),
        url: createURL('/mp/take-a-tour/', { ut_source: 'megamenu' })
      },
      {
        title: t(NAVSUBMENUCOPY.AUDIENCE),
        subTitle: t(NAVSUBMENUCOPY.AUDIENCE_SUBTITLE),
        url: createURL('/mp/audience/', { ut_source: 'megamenu' })
      }
    ],
    lang === 'fr' && [
      {
        title: t(NAVSUBMENUCOPY.FORMS_AND_WORKFLOWS)
      },
      {
        title: 'Apply',
        subTitle: t(NAVSUBMENUCOPY.APPLY_SUBTITLE),
        url: createURL('https://apply.surveymonkey.com/', {
          ut_source: 'megamenu'
        })
      },
      {
        title: 'Usabilla',
        subtitle: t(NAVSUBMENUCOPY.USABILLA_SUBTITLE),
        url: createURL(usabillaLink(lang), {
          utm_source: 'megamenu'
        })
      }
    ],
    !['fr', 'en'].includes(lang) && [
      {
        title: 'SurveyMonkey',
        subTitle: t(NAVSUBMENUCOPY.SURVEYMONKEY_SUBTITLE),
        url: createURL('/mp/take-a-tour/', { ut_source: 'megamenu' })
      }
    ],
    !['fr', 'en'].includes(lang) && [
      {
        title: t(NAVSUBMENUCOPY.AUDIENCE),
        subTitle: t(NAVSUBMENUCOPY.AUDIENCE_SUBTITLE),
        url: createURL('/mp/audience/', { ut_source: 'megamenu' })
      },
      {
        title: 'Usabilla',
        subTitle: t(NAVSUBMENUCOPY.USABILLA_SUBTITLE),
        url: createURL(usabillaLink(lang), {
          utm_source: 'megamenu'
        })
      }
    ]
  ].filter(Boolean);
};

/**
 * Get all Solution Items
 * @param {String} lang
 * @return {Array}
 */
export const getSolutionsItems = lang => {
  return [
    lang === 'en' && [
      {
        title: 'People Powered Data for business'
      },
      {
        title: 'Customers',
        subTitle: 'Win more business with Customer Powered Data',
        url: createURL('/mp/customer-feedback/', { ut_source: 'megamenu' })
      },
      {
        title: 'Employees',
        subTitle: 'Build a stronger workforce with Employee Powered Data',
        url: createURL('/mp/employee-feedback/', { ut_source: 'megamenu' })
      },
      {
        title: 'Markets',
        subTitle: 'Validate business strategy with Market Powered Data',
        url: createURL('/mp/market-research/', { ut_source: 'megamenu' })
      }
    ],
    lang === 'en' && [
      {
        title: 'Solutions for teams'
      },
      {
        title: 'Customer Experience',
        subTitle: 'Delight customers & increase loyalty through feedback',
        url: createURL('/mp/customer-experience-solutions/', {
          ut_source: 'megamenu'
        })
      },
      {
        title: 'Human Resources',
        subTitle: 'Improve your employee experience, engagement & retention',
        url: createURL('/mp/hr-solutions/', { ut_source: 'megamenu' })
      },
      {
        title: 'Marketing',
        subTitle: 'Create winning campaigns, boost ROI & drive growth',
        url: createURL('/mp/marketing-solutions/', { ut_source: 'megamenu' })
      }
    ],
    lang !== 'en' && [
      {
        title: t(NAVSUBMENUCOPY.CUSTOMERS),
        subTitle: t(NAVSUBMENUCOPY.CUSTOMERS_SUBTITLE),
        url: createURL('/mp/customer-feedback/', { ut_source: 'megamenu' })
      }
    ],
    lang !== 'en' && [
      {
        title: t(NAVSUBMENUCOPY.EMPLOYEES),
        subTitle: t(NAVSUBMENUCOPY.EMPLOYEES_SUBTITLE),
        url: createURL('/mp/employee-feedback/', { ut_source: 'megamenu' })
      }
    ],
    lang !== 'en' && [
      {
        title: t(NAVSUBMENUCOPY.MARKETS),
        subTitle: t(NAVSUBMENUCOPY.MARKETS_SUBTITLE),
        url: createURL('/mp/market-research/', { ut_source: 'megamenu' })
      }
    ]
  ].filter(Boolean);
};

/**
 * Return additonal product links
 * @param {string} lang
 * @return {Array}
 */
export const getProductsLinks = lang => {
  return [
    {
      title:
        lang === 'en' ? 'View all products' : t(NAVSUBMENUCOPY.PRODUCTS_LINK),
      alignment: 'right',
      url: createURL(lang === 'en' ? '/what-is-surveymonkey/' : '/business/', {
        ut_source: 'megamenu'
      })
    },
    lang !== 'en' && {
      title: t(NAVSUBMENUCOPY.INTEGRATIONS_PLUGINS_LINK),
      alignment: 'left',
      url: createURL('/apps/', { ut_source: 'megamenu' })
    }
  ];
};

/**
 * Return the Survey Types Link
 * @param {string} lang
 * @return {Array}
 */
export const getSolutionsLinks = lang => {
  return [
    {
      title:
        lang !== 'en'
          ? t(NAVSUBMENUCOPY.VIEW_MORE_LINK)
          : 'Explore more survey types',
      alignment: lang !== 'en' ? 'right' : 'left',
      url: createURL('/mp/survey-types/', { ut_source: 'megamenu' })
    }
  ];
};

/**
 * Returns survey types links
 * @return {Array}
 */
export const getSolutionsByNeedItems = () => {
  return [
    [
      {
        title: t(NAVSUBMENUCOPY.CUSTOMER_SATISFACTION),
        url: createURL('/mp/customer-satisfaction-surveys/', {
          ut_source: 'megamenu'
        })
      },
      {
        title: t(NAVSUBMENUCOPY.CUSTOMER_LOYALTY),
        url: createURL('/mp/customer-loyalty-surveys/', {
          ut_source: 'megamenu'
        })
      },
      {
        title: t(NAVSUBMENUCOPY.EVENT_SURVEYS),
        url: createURL('/mp/event-planning-surveys/', { ut_source: 'megamenu' })
      }
    ],
    [
      {
        title: t(NAVSUBMENUCOPY.EMPLOYEE_ENGAGEMENT),
        url: createURL('/mp/employee-surveys/', { ut_source: 'megamenu' })
      },
      {
        title: t(NAVSUBMENUCOPY.JOB_SATISFACTION),
        url: createURL('/mp/job-satisfaction-survey/', {
          ut_source: 'megamenu'
        })
      },
      {
        title: t(NAVSUBMENUCOPY.HR_SURVEYS),
        url: createURL('/mp/human-resource-surveys/', { ut_source: 'megamenu' })
      }
    ],
    [
      {
        title: t(NAVSUBMENUCOPY.MARKET_RESEARCH),
        url: createURL('/mp/market-research-surveys/', {
          ut_source: 'megamenu'
        })
      },
      {
        title: t(NAVSUBMENUCOPY.OPINION_POLLS),
        url: createURL('/mp/online-polls/', { ut_source: 'megamenu' })
      },
      {
        title: t(NAVSUBMENUCOPY.CONCEPT_TESTING),
        url: createURL('/mp/concept-testing/', { ut_source: 'megamenu' })
      }
    ]
  ].filter(Boolean);
};

/**
 * Get all resource submenu items
 * @param {string} lang
 * @return {Array}
 */
export const getResourcesItems = lang => {
  return [
    lang === 'en' && [
      {
        title: 'Resources',
        subTitle: 'Best practices for using surveys & survey data',
        url: createURL('/resources/', { ut_source: 'megamenu' })
      }
    ],
    showBlog(lang) && [
      {
        title: t(NAVSUBMENUCOPY.BLOG),
        subTitle: t(NAVSUBMENUCOPY.BLOG_SUBTITLE),
        url: createURL('/curiosity/', { ut_source: 'megamenu' })
      }
    ],
    showBlog(lang) && [
      {
        title: t(NAVBARCOPY.HELP_CENTER),
        subTitle: t(NAVSUBMENUCOPY.HELP_CENTER_SUBTITLE),
        url: createURL('https://help.surveymonkey.com/', {
          ut_source: 'megamenu'
        })
      }
    ]
  ].filter(Boolean);
};

/**
 * If EN returns additional link for Resources submenu
 * @param {string} lang
 * @return {Array}
 */
export const getResourcesLinks = lang => {
  return [
    lang === 'en' && {
      title: 'Explore our 180+ survey templates',
      alignment: 'right',
      url: createURL('/mp/sample-survey-questionnaire-templates/', {
        ut_source: 'megamenu'
      })
    }
  ].filter(Boolean);
};

/**
 * Returns all submenus for a given title and lang
 * @param {string} submenuKey
 * @param {string} lang
 * @param {Boolean} isEUDC
 * @return {Object}
 */
export const getNavSubmenuItems = (submenuKey, lang, isEUDC) =>
  ({
    Products: getProductsItems(lang, isEUDC),
    Solutions: getSolutionsItems(lang),
    Resources: getResourcesItems(lang)
  }[submenuKey]);

/**
 * All nested mobile items
 * @param {string} lang
 * @param {Boolean} isEUDC
 * @return {Array}
 */
export const getNestedItemsMobile = (lang, isEUDC) => {
  const productsNestedItems = []
    .concat(...getProductsItems(lang, isEUDC))
    .concat(...getProductsLinks(lang))
    .filter(Boolean);
  const solutionsNestedItems = []
    .concat(...getSolutionsItems(lang))
    .concat(...getSolutionsByNeedItems(lang))
    .concat(...getSolutionsLinks(lang))
    .filter(Boolean);
  const resourcesNestedItems = []
    .concat(...getResourcesItems(lang))
    .concat(...getResourcesLinks(lang))
    .filter(Boolean);
  return [
    {
      title: t(NAVBARCOPY.PRODUCTS),
      nestedItems: productsNestedItems
    },
    {
      title: t(NAVBARCOPY.SOLUTIONS),
      nestedItems: solutionsNestedItems
    },
    ['de', 'en'].includes(lang) && {
      title: t(NAVBARCOPY.RESOURCES),
      nestedItems: resourcesNestedItems
    }
  ].filter(Boolean);
};

/**
 * All non-nested mobile items
 * @param {string} lang
 * @return {Array}
 */
export const getNonNestedItemsMobile = lang => {
  return [
    !['de', 'en'].includes(lang) && {
      title: t(NAVBARCOPY.HELP_CENTER),
      url: createURL('https://help.surveymonkey.com/', {
        ut_source: 'megamenu'
      })
    },
    {
      title: t(NAVBARCOPY.PLANS_AND_PRICING),
      url: createURL('/pricing/', { ut_source: 'megamenu' })
    }
  ].filter(Boolean);
};
